import { useMemo, type MutableRefObject } from 'react'
import type {
  BotProtectionRuntimeEvent,
  BotProtectionWidgetCallbacks,
  BotProtectionWidgetProps,
} from './types'
import type { BotProtectionRuntimeEventLogger } from './runtimeEventLogger'
import type { BotProtectionRetryPolicy } from './widgetRetryPolicy'
import type { BotProtectionRuntimeStateController } from './widgetRuntimeState'

type WidgetEventHandlerInput = {
  logRuntimeEvent: BotProtectionRuntimeEventLogger
  onTokenChangeRef: MutableRefObject<BotProtectionWidgetProps['onTokenChange']>
  retryPolicy: Pick<BotProtectionRetryPolicy, 'resetAttempts' | 'scheduleReset'>
  runtimeState: Pick<BotProtectionRuntimeStateController, 'setRuntimeState'>
}

type WidgetEventHandlers = {
  callbacks: BotProtectionWidgetCallbacks
  onRuntimeEvent: (event: BotProtectionRuntimeEvent) => void
}

export const useBotProtectionWidgetEventHandlers = ({
  logRuntimeEvent,
  onTokenChangeRef,
  retryPolicy,
  runtimeState,
}: WidgetEventHandlerInput): WidgetEventHandlers =>
  useMemo(
    () => ({
      callbacks: {
        onToken: (token) => {
          retryPolicy.resetAttempts()
          runtimeState.setRuntimeState('healthy')
          onTokenChangeRef.current(token)
        },
        onExpired: () => {
          onTokenChangeRef.current('')
          runtimeState.setRuntimeState('degraded', { reason: 'expired' })
          logRuntimeEvent('info', 'bot_protection.token_expired')
          retryPolicy.scheduleReset('expired')
        },
        onError: (errorCode?: string) => {
          onTokenChangeRef.current('')
          runtimeState.setRuntimeState('degraded', { reason: 'error', errorCode })
          logRuntimeEvent('warn', 'bot_protection.challenge_error', {
            errorCode: errorCode || 'unknown_error',
          })
          retryPolicy.scheduleReset('error', errorCode)
        },
        onTimeout: () => {
          onTokenChangeRef.current('')
          runtimeState.setRuntimeState('degraded', { reason: 'timeout' })
          logRuntimeEvent('warn', 'bot_protection.challenge_timeout')
          retryPolicy.scheduleReset('timeout')
        },
      },
      onRuntimeEvent: ({ level, event, context }) => {
        if (event === 'turnstile.loader_ready') {
          runtimeState.setRuntimeState('script_ready')
        }
        logRuntimeEvent(level, event, context)
      },
    }),
    [logRuntimeEvent, onTokenChangeRef, retryPolicy, runtimeState],
  )
